import React, { useEffect, useMemo, useRef, useState } from "react";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ensurePanelAdminSession } from "../utils/panelSession";

function defaults() {
  return {
    paymentGateway: {
      provider: "razorpay",
      enabled: true,
      razorpayKeyId: "",
      razorpayKeySecret: "",
      webhookSecret: "",
    },
    apiAccess: {
      externalApiEnabled: false,
      partnerApiEnabled: false,
      allowedApis: [],
      allowedOrigins: [],
      allowedApiKeys: [],
    },
    authkey: {
      apiKey: "",
      defaultCountryCode: "91",
      smsSender: "",
      whatsappBrandNumber: "",
      enabled: true,
    },
    developerMode: {
      enabled: false,
      phone: "8723015588",
      otp: "872301",
    },
    contentFeed: {
      post: defaultFeedVisibility(),
      reel: defaultFeedVisibility(),
      story: defaultFeedVisibility(),
    },
  };
}

function defaultFeedVisibility() {
  return {
    userViewer: {
      allUsers: true,
      allArtists: true,
      followingUsers: true,
      followingArtists: true,
      interestUsers: true,
      interestArtists: true,
    },
    artistViewer: {
      allUsers: true,
      allArtists: true,
      followingUsers: true,
      followingArtists: true,
      interestUsers: true,
      interestArtists: true,
    },
  };
}

function mergeSettingsShape(raw = {}) {
  const base = defaults();
  return {
    ...base,
    ...raw,
    paymentGateway: { ...base.paymentGateway, ...(raw.paymentGateway || {}) },
    apiAccess: { ...base.apiAccess, ...(raw.apiAccess || {}) },
    authkey: { ...base.authkey, ...(raw.authkey || {}) },
    developerMode: { ...base.developerMode, ...(raw.developerMode || {}) },
    contentFeed: {
      post: { ...base.contentFeed.post, ...((raw.contentFeed || {}).post || {}) },
      reel: { ...base.contentFeed.reel, ...((raw.contentFeed || {}).reel || {}) },
      story: { ...base.contentFeed.story, ...((raw.contentFeed || {}).story || {}) },
    },
  };
}

function safeEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function toList(input) {
  return Array.from(
    new Set(
      String(input || "")
        .split(/[\n,]/g)
        .map((x) => x.trim())
        .filter(Boolean)
    )
  );
}

function toText(list) {
  return Array.isArray(list) ? list.join("\n") : "";
}

function parseJsonObject(raw, label) {
  const text = String(raw || "").trim();
  if (!text) return {};
  let parsed = {};
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(`${label} must be valid JSON`);
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error(`${label} must be JSON object`);
  }
  return parsed;
}

export default function Settings() {
  const employee = useMemo(() => safeEmployee(), []);
  const role = String(employee?.role || "").toLowerCase();
  const isSuperAdmin = role === "super_admin";
  const perms = Array.isArray(employee?.permissions) ? employee.permissions : [];

  const canView = isSuperAdmin || perms.some((p) => [
    "view_settings",
    "manage_settings",
    "manage_payment_settings",
    "manage_api_settings",
    "manage_authkey_settings",
    "view_authkey_templates",
    "view_authkey_balance",
    "send_authkey_broadcast",
  ].includes(p));
  const canManageSettings = isSuperAdmin || perms.includes("manage_settings");
  const canManagePayment = canManageSettings || perms.includes("manage_payment_settings");
  const canManageApi = canManageSettings || perms.includes("manage_api_settings");
  const canManageAuthkey = canManageSettings || perms.includes("manage_authkey_settings");
  const canBroadcast = canManageSettings || canManageAuthkey || perms.includes("send_authkey_broadcast");
  const canViewTemplates = canManageAuthkey || perms.includes("view_authkey_templates");
  const canViewBalance = canManageAuthkey || perms.includes("view_authkey_balance");

  const app = useMemo(() => {
    try {
      return getApp();
    } catch {
      return null;
    }
  }, []);
  const functions = useMemo(() => (app ? getFunctions(app, "asia-south1") : null), [app]);

  const [settings, setSettings] = useState(defaults());
  const [templates, setTemplates] = useState([]);
  const [balance, setBalance] = useState(null);
  const [notice, setNotice] = useState({ type: "", text: "" });
  const [popup, setPopup] = useState({ open: false, title: "", message: "", type: "ok" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const noticeTimerRef = useRef(null);
  const popupTimerRef = useRef(null);

  const [channel, setChannel] = useState("whatsapp");
  const [targetMode, setTargetMode] = useState("all");
  const [numbersInput, setNumbersInput] = useState("");
  const [waWid, setWaWid] = useState("");
  const [waType, setWaType] = useState("text");
  const [waBodyValues, setWaBodyValues] = useState("{}");
  const [waHeaderValues, setWaHeaderValues] = useState("{}");
  const [smsMessage, setSmsMessage] = useState("");
  const [smsSid, setSmsSid] = useState("");
  const [smsPeId, setSmsPeId] = useState("");
  const [smsTemplateId, setSmsTemplateId] = useState("");
  const [smsUnicode, setSmsUnicode] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const adminContext = useMemo(() => ({
    docId: String(employee?.docId || ""),
    role: String(employee?.role || ""),
    mobile: String(employee?.mobile || ""),
  }), [employee?.docId, employee?.mobile, employee?.role]);

  const ping = (text, type = "ok") => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    setNotice({ text, type });
    noticeTimerRef.current = setTimeout(() => setNotice({ text: "", type: "" }), 3500);
  };

  const closePopup = () => {
    if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    setPopup({ open: false, title: "", message: "", type: "ok" });
  };

  const showPopup = (title, message, type = "ok", autoCloseMs = 3200) => {
    if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    setPopup({ open: true, title, message, type });
    popupTimerRef.current = setTimeout(() => {
      setPopup({ open: false, title: "", message: "", type: "ok" });
    }, autoCloseMs);
  };

  const applyIncomingSettings = (incoming = {}) => {
    setSettings((prev) => mergeSettingsShape({
      ...prev,
      ...incoming,
      paymentGateway: { ...prev.paymentGateway, ...(incoming.paymentGateway || {}) },
      apiAccess: { ...prev.apiAccess, ...(incoming.apiAccess || {}) },
      authkey: { ...prev.authkey, ...(incoming.authkey || {}) },
      developerMode: { ...prev.developerMode, ...(incoming.developerMode || {}) },
      contentFeed: {
        post: { ...prev.contentFeed.post, ...((incoming.contentFeed || {}).post || {}) },
        reel: { ...prev.contentFeed.reel, ...((incoming.contentFeed || {}).reel || {}) },
        story: { ...prev.contentFeed.story, ...((incoming.contentFeed || {}).story || {}) },
      },
    }));
  };

  const authkeyStatusMessage = (data) => {
    if (!data?.ok) {
      return String(
        data?.raw?.message ||
        data?.raw?.Message ||
        data?.error ||
        "Authkey connection check failed."
      );
    }
    return `Authkey connected. Balance: ${data?.balance ?? "-"}.`;
  };

  const refreshSettingsSnapshot = async () => {
    const data = await call("getPanelSettings");
    if (data?.settings) {
      applyIncomingSettings(data.settings);
      return data.settings;
    }
    return null;
  };

  const call = async (name, payload = {}) => {
    if (!functions) throw new Error("Cloud Functions not initialized");
    const { employee: sessionEmployee } = await ensurePanelAdminSession({ forceRefresh: true });
    const fn = httpsCallable(functions, name);
    const res = await fn({
      ...payload,
      adminContext: {
        ...adminContext,
        docId: String(sessionEmployee?.docId || adminContext.docId || ""),
        role: String(sessionEmployee?.role || adminContext.role || ""),
        mobile: String(sessionEmployee?.mobile || adminContext.mobile || ""),
      },
    });
    return res?.data || {};
  };

  useEffect(() => () => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
  }, []);

  const loadSettings = async () => {
    if (!canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await call("getPanelSettings");
      if (data?.settings) {
        setSettings(mergeSettingsShape(data.settings));
      }
    } catch (e) {
      ping(e?.message || "Failed to load settings", "err");
      showPopup("Settings load failed", e?.message || "Failed to load settings", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSection = async (section) => {
    setBusy(section);
    try {
      const updates = {};
      if (section === "payment") updates.paymentGateway = settings.paymentGateway;
      if (section === "api") updates.apiAccess = settings.apiAccess;
      if (section === "authkey") updates.authkey = settings.authkey;
      if (section === "developerMode") updates.developerMode = settings.developerMode;
      const res = await call("updatePanelSettings", { updates });
      let liveSettings = res?.settings || null;
      if (res?.settings) {
        applyIncomingSettings(res.settings);
      }
      try {
        liveSettings = await refreshSettingsSnapshot() || liveSettings;
      } catch {
        // Keep the just-saved response if a follow-up refresh fails.
      }

      let title = "Settings saved";
      let message = "Settings updated successfully.";
      let type = "ok";

      if (section === "authkey") {
        if (Boolean(liveSettings?.authkey?.enabled ?? settings.authkey.enabled)) {
          const balanceData = await call("fetchAuthkeyBalance");
          setBalance(balanceData);
          title = balanceData?.ok ? "Authkey saved" : "Authkey saved with warning";
          message = authkeyStatusMessage(balanceData);
          type = balanceData?.ok ? "ok" : "err";
        } else {
          title = "Authkey saved";
          message = "Authkey settings saved while messaging remains disabled.";
        }
      } else if (section === "payment") {
        message = "Payment gateway settings updated successfully.";
      } else if (section === "api") {
        message = "API access settings updated successfully.";
      } else if (section === "developerMode") {
        const enabled = Boolean(res?.settings?.developerMode?.enabled ?? settings.developerMode.enabled);
        title = enabled ? "Developer mode enabled" : "Developer mode disabled";
        message = enabled ?
          `Phone ${settings.developerMode.phone} can now log in with OTP ${settings.developerMode.otp}.` :
          'Developer mode is off. The app will show "developer mode disble connect admin".';
      }

      ping(message, type);
      showPopup(title, message, type);
      return { ok: true };
    } catch (e) {
      const message = e?.message || "Save failed";
      ping(message, "err");
      showPopup("Save failed", message, "err");
      return { ok: false };
    } finally {
      setBusy("");
    }
  };

  const toggleDeveloperMode = async () => {
    const nextEnabled = !Boolean(settings.developerMode.enabled);
    const nextDeveloperMode = { ...settings.developerMode, enabled: nextEnabled };
    setBusy("developerMode");
    try {
      const res = await call("updatePanelSettings", {
        updates: { developerMode: nextDeveloperMode },
      });
      let liveSettings = res?.settings || null;
      if (res?.settings) {
        applyIncomingSettings(res.settings);
      }
      try {
        liveSettings = await refreshSettingsSnapshot() || liveSettings;
      } catch {
        // Keep the callable response if the follow-up refresh fails.
      }
      const enabled = Boolean(liveSettings?.developerMode?.enabled ?? nextEnabled);
      const title = enabled ? "Developer mode enabled" : "Developer mode disabled";
      const message = enabled ?
        `Phone ${nextDeveloperMode.phone} now uses OTP ${nextDeveloperMode.otp}. Changes apply immediately.` :
        'Developer mode is now off. The app will show "developer mode disble connect admin".';
      ping(message, "ok");
      showPopup(title, message, "ok");
    } catch (e) {
      const message = e?.message || "Developer mode update failed";
      ping(message, "err");
      showPopup("Developer mode update failed", message, "err");
    } finally {
      setBusy("");
    }
  };

  const toggleAuthkeyEnabled = async () => {
    const nextEnabled = !Boolean(settings.authkey.enabled);
    const nextAuthkey = { ...settings.authkey, enabled: nextEnabled };
    setBusy("authkeyToggle");
    try {
      const res = await call("updatePanelSettings", {
        updates: { authkey: nextAuthkey },
      });
      let liveSettings = res?.settings || null;
      if (res?.settings) {
        applyIncomingSettings(res.settings);
      }
      try {
        liveSettings = await refreshSettingsSnapshot() || liveSettings;
      } catch {
        // Keep the callable response if the follow-up refresh fails.
      }

      const enabled = Boolean(liveSettings?.authkey?.enabled ?? nextEnabled);
      let title = enabled ? "Authkey enabled" : "Authkey disabled";
      let message = enabled ?
        "Authkey messaging is enabled again." :
        "Authkey messaging is now disabled for app OTP and admin broadcast sends.";
      let type = "ok";

      if (enabled) {
        const balanceData = await call("fetchAuthkeyBalance");
        setBalance(balanceData);
        title = balanceData?.ok ? "Authkey enabled" : "Authkey enabled with warning";
        message = authkeyStatusMessage(balanceData);
        type = balanceData?.ok ? "ok" : "err";
      }

      ping(message, type);
      showPopup(title, message, type);
    } catch (e) {
      const message = e?.message || "Authkey status update failed";
      ping(message, "err");
      showPopup("Authkey update failed", message, "err");
    } finally {
      setBusy("");
    }
  };

  const loadTemplates = async () => {
    setBusy("templates");
    try {
      const data = await call("fetchAuthkeyTemplates", { channel: "whatsapp" });
      if (!data?.ok) throw new Error(data?.error || "Template fetch failed");
      const list = Array.isArray(data.templates) ? data.templates : [];
      setTemplates(list);
      if (!waWid && list.length) setWaWid(String(list[0].wid || ""));
      const message = `Loaded ${list.length} Authkey templates.`;
      ping(message);
      showPopup("Templates loaded", message, "ok");
    } catch (e) {
      setTemplates([]);
      const message = e?.message || "Template fetch failed";
      ping(message, "err");
      showPopup("Template fetch failed", message, "err");
    } finally {
      setBusy("");
    }
  };

  const loadBalance = async () => {
    setBusy("balance");
    try {
      const data = await call("fetchAuthkeyBalance");
      setBalance(data);
      if (!data?.ok) throw new Error(data?.raw?.message || data?.raw?.Message || "Balance fetch failed");
      const message = authkeyStatusMessage(data);
      ping(message);
      showPopup("Balance fetched", message, "ok");
    } catch (e) {
      const message = e?.message || "Balance fetch failed";
      ping(message, "err");
      showPopup("Balance fetch failed", message, "err");
    } finally {
      setBusy("");
    }
  };

  const sendBroadcast = async () => {
    setBusy("broadcast");
    setSendResult(null);
    try {
      const shouldSendWhatsapp = channel === "whatsapp" || channel === "both";
      const payload = {
        channel,
        targetMode,
        numbers: toList(numbersInput),
        countryCode: settings.authkey.defaultCountryCode || "91",
        whatsapp: {
          wid: waWid,
          type: waType,
          bodyValues: shouldSendWhatsapp ?
            parseJsonObject(waBodyValues, "WhatsApp body values") :
            {},
          headerValues: shouldSendWhatsapp ?
            parseJsonObject(waHeaderValues, "WhatsApp header values") :
            {},
        },
        sms: {
          message: smsMessage,
          sender: settings.authkey.smsSender || "",
          sid: smsSid,
          peId: smsPeId,
          templateId: smsTemplateId,
          isUnicode: smsUnicode,
        },
      };
      const data = await call("sendAuthkeyBroadcast", payload);
      setSendResult(data);
      const message = `Broadcast completed for ${data?.totalRecipients || 0} recipients.`;
      ping(message);
      showPopup("Broadcast sent", message, "ok");
    } catch (e) {
      const message = e?.message || "Broadcast failed";
      ping(message, "err");
      showPopup("Broadcast failed", message, "err");
    } finally {
      setBusy("");
    }
  };

  if (!canView) {
    return <div style={sx.center}>Access denied for Settings.</div>;
  }
  if (loading) {
    return <div style={sx.center}>Loading settings...</div>;
  }

  return (
    <div style={sx.page}>
      <h2>Settings</h2>
      {notice.text ? (
        <div style={{ ...sx.notice, ...(notice.type === "err" ? sx.err : sx.ok) }}>{notice.text}</div>
      ) : null}
      {popup.open ? (
        <div style={{ ...sx.popupCard, ...(popup.type === "err" ? sx.popupErr : sx.popupOk) }}>
          <div style={sx.popupBody}>
            <strong>{popup.title}</strong>
            <div style={sx.popupText}>{popup.message}</div>
          </div>
          <button type="button" onClick={closePopup} style={sx.popupClose} aria-label="Close popup">
            x
          </button>
        </div>
      ) : null}

      <section style={sx.card}>
        <h3>1. Payment Gateway Setup</h3>
        <div style={sx.grid}>
          <input style={sx.input} value={settings.paymentGateway.provider} onChange={(e) => setSettings((p) => ({ ...p, paymentGateway: { ...p.paymentGateway, provider: e.target.value } }))} placeholder="Provider" disabled={!canManagePayment} />
          <input style={sx.input} value={settings.paymentGateway.razorpayKeyId} onChange={(e) => setSettings((p) => ({ ...p, paymentGateway: { ...p.paymentGateway, razorpayKeyId: e.target.value } }))} placeholder="Razorpay Key ID" disabled={!canManagePayment} />
          <input style={sx.input} value={settings.paymentGateway.razorpayKeySecret} onChange={(e) => setSettings((p) => ({ ...p, paymentGateway: { ...p.paymentGateway, razorpayKeySecret: e.target.value } }))} placeholder="Razorpay Key Secret" disabled={!canManagePayment} />
          <input style={sx.input} value={settings.paymentGateway.webhookSecret} onChange={(e) => setSettings((p) => ({ ...p, paymentGateway: { ...p.paymentGateway, webhookSecret: e.target.value } }))} placeholder="Webhook Secret" disabled={!canManagePayment} />
          <label><input type="checkbox" checked={Boolean(settings.paymentGateway.enabled)} onChange={(e) => setSettings((p) => ({ ...p, paymentGateway: { ...p.paymentGateway, enabled: e.target.checked } }))} disabled={!canManagePayment} /> Enabled</label>
        </div>
        <button style={sx.btn} disabled={!canManagePayment || busy === "payment"} onClick={() => saveSection("payment")}>{busy === "payment" ? "Saving..." : "Save Payment"}</button>
      </section>

      <section style={sx.card}>
        <h3>API Access (allow external APIs)</h3>
        <div style={sx.grid}>
          <label><input type="checkbox" checked={Boolean(settings.apiAccess.externalApiEnabled)} onChange={(e) => setSettings((p) => ({ ...p, apiAccess: { ...p.apiAccess, externalApiEnabled: e.target.checked } }))} disabled={!canManageApi} /> External APIs enabled</label>
          <label><input type="checkbox" checked={Boolean(settings.apiAccess.partnerApiEnabled)} onChange={(e) => setSettings((p) => ({ ...p, apiAccess: { ...p.apiAccess, partnerApiEnabled: e.target.checked } }))} disabled={!canManageApi} /> Partner APIs enabled</label>
          <textarea style={sx.textarea} value={toText(settings.apiAccess.allowedApis)} onChange={(e) => setSettings((p) => ({ ...p, apiAccess: { ...p.apiAccess, allowedApis: toList(e.target.value) } }))} disabled={!canManageApi} placeholder="Allowed API ids (one per line)" />
          <textarea style={sx.textarea} value={toText(settings.apiAccess.allowedOrigins)} onChange={(e) => setSettings((p) => ({ ...p, apiAccess: { ...p.apiAccess, allowedOrigins: toList(e.target.value) } }))} disabled={!canManageApi} placeholder="Allowed origins (one per line)" />
          <textarea style={sx.textarea} value={toText(settings.apiAccess.allowedApiKeys)} onChange={(e) => setSettings((p) => ({ ...p, apiAccess: { ...p.apiAccess, allowedApiKeys: toList(e.target.value) } }))} disabled={!canManageApi} placeholder="Allowed API keys (one per line)" />
        </div>
        <button style={sx.btn} disabled={!canManageApi || busy === "api"} onClick={() => saveSection("api")}>{busy === "api" ? "Saving..." : "Save API Access"}</button>
      </section>

      <section style={sx.card}>
        <h3>2. Authkey Setup</h3>
        <div style={sx.grid}>
          <input style={sx.input} value={settings.authkey.apiKey} onChange={(e) => setSettings((p) => ({ ...p, authkey: { ...p.authkey, apiKey: e.target.value } }))} disabled={!canManageAuthkey} placeholder="Authkey API key" />
          <input style={sx.input} value={settings.authkey.defaultCountryCode} onChange={(e) => setSettings((p) => ({ ...p, authkey: { ...p.authkey, defaultCountryCode: e.target.value.replace(/\D/g, "").slice(0, 4) } }))} disabled={!canManageAuthkey} placeholder="Country code" />
          <input style={sx.input} value={settings.authkey.smsSender} onChange={(e) => setSettings((p) => ({ ...p, authkey: { ...p.authkey, smsSender: e.target.value } }))} disabled={!canManageAuthkey} placeholder="SMS sender" />
          <input style={sx.input} value={settings.authkey.whatsappBrandNumber} onChange={(e) => setSettings((p) => ({ ...p, authkey: { ...p.authkey, whatsappBrandNumber: e.target.value } }))} disabled={!canManageAuthkey} placeholder="WhatsApp brand number" />
          <div style={sx.statusBlock}>
            <div style={{ ...sx.statusPill, ...(settings.authkey.enabled ? sx.statusOn : sx.statusOff) }}>
              {settings.authkey.enabled ? "Authkey enabled" : "Authkey disabled"}
            </div>
            <div style={sx.meta}>
              {balance
                ? authkeyStatusMessage(balance)
                : "Save or check balance to verify the Authkey connection."}
            </div>
            <div style={sx.meta}>
              Authkey controls normal OTP sending for all regular app users.
            </div>
          </div>
        </div>
        <div style={sx.row}>
          <button style={sx.btn} disabled={!canManageAuthkey || busy === "authkey"} onClick={() => saveSection("authkey")}>{busy === "authkey" ? "Saving..." : "Save Authkey"}</button>
          <button
            style={{ ...sx.toggleBtn, ...(settings.authkey.enabled ? sx.toggleWarn : sx.toggleSafe) }}
            disabled={!canManageAuthkey || busy === "authkeyToggle"}
            onClick={toggleAuthkeyEnabled}
          >
            {busy === "authkeyToggle"
              ? "Updating..."
              : settings.authkey.enabled
              ? "Disable Authkey"
              : "Enable Authkey"}
          </button>
          <button style={sx.btnAlt} disabled={!canViewTemplates || busy === "templates"} onClick={loadTemplates}>{busy === "templates" ? "Loading..." : "Load Templates"}</button>
          <button style={sx.btnAlt} disabled={!canViewBalance || busy === "balance"} onClick={loadBalance}>{busy === "balance" ? "Checking..." : "Check Balance"}</button>
        </div>
        <div style={sx.meta}>
          If Authkey is disabled, normal OTP send is blocked for everyone except the developer-mode test number.
        </div>
      </section>

      <section style={sx.card}>
        <h3>3. Developer Mode</h3>
        <div style={sx.grid}>
          <input style={sx.input} value={settings.developerMode.phone} readOnly placeholder="Developer phone" />
          <input style={sx.input} value={settings.developerMode.otp} readOnly placeholder="Developer OTP" />
          <div style={sx.statusBlock}>
            <div style={{ ...sx.statusPill, ...(settings.developerMode.enabled ? sx.statusOn : sx.statusOff) }}>
              {settings.developerMode.enabled ? "Developer mode live" : "Developer mode off"}
            </div>
            <div style={sx.meta}>Only this number gets bypass access: 8723015588</div>
            <div style={sx.meta}>Fixed OTP for that number: 872301</div>
            <div style={sx.meta}>Disabled app message: developer mode disble connect admin</div>
          </div>
        </div>
        <div style={sx.row}>
          <button
            style={{ ...sx.toggleBtn, ...(settings.developerMode.enabled ? sx.toggleWarn : sx.toggleSafe) }}
            disabled={!canManageAuthkey || busy === "developerMode"}
            onClick={toggleDeveloperMode}
          >
            {busy === "developerMode"
              ? "Updating..."
              : settings.developerMode.enabled
              ? "Disable Developer Mode"
              : "Enable Developer Mode"}
          </button>
          <div style={sx.meta}>All other phone numbers continue using the normal OTP flow.</div>
        </div>
      </section>

      <section style={sx.card}>
        <h3>WhatsApp Broadcast</h3>
        <div style={sx.grid}>
          <select style={sx.input} value={channel} onChange={(e) => setChannel(e.target.value)} disabled={!canBroadcast}>
            <option value="whatsapp">WhatsApp</option>
            <option value="sms">SMS</option>
            <option value="both">WhatsApp + SMS</option>
          </select>
          <select style={sx.input} value={targetMode} onChange={(e) => setTargetMode(e.target.value)} disabled={!canBroadcast}>
            <option value="all">All Users + Artists</option>
            <option value="all_users">All Users</option>
            <option value="all_artists">All Artists</option>
            <option value="numbers">Custom numbers</option>
          </select>
          <input style={sx.input} value={waWid} onChange={(e) => setWaWid(e.target.value)} disabled={!canBroadcast} placeholder="WhatsApp template WID" />
          <select style={sx.input} value={waType} onChange={(e) => setWaType(e.target.value)} disabled={!canBroadcast}>
            <option value="text">Template text</option>
            <option value="media">Template media</option>
          </select>
          <textarea style={sx.textarea} value={numbersInput} onChange={(e) => setNumbersInput(e.target.value)} disabled={!canBroadcast || targetMode !== "numbers"} placeholder="Custom numbers: comma/newline separated" />
          <textarea style={sx.textarea} value={waBodyValues} onChange={(e) => setWaBodyValues(e.target.value)} disabled={!canBroadcast} placeholder='WhatsApp bodyValues JSON, e.g. {"1":"Aman"}' />
          <textarea style={sx.textarea} value={waHeaderValues} onChange={(e) => setWaHeaderValues(e.target.value)} disabled={!canBroadcast} placeholder='WhatsApp headerValues JSON, e.g. {"headerData":"https://..."}' />
          <textarea style={sx.textarea} value={smsMessage} onChange={(e) => setSmsMessage(e.target.value)} disabled={!canBroadcast} placeholder="SMS message (for sms/both)" />
          <input style={sx.input} value={smsSid} onChange={(e) => setSmsSid(e.target.value)} disabled={!canBroadcast} placeholder="SMS SID (optional)" />
          <input style={sx.input} value={smsPeId} onChange={(e) => setSmsPeId(e.target.value)} disabled={!canBroadcast} placeholder="PE ID (optional)" />
          <input style={sx.input} value={smsTemplateId} onChange={(e) => setSmsTemplateId(e.target.value)} disabled={!canBroadcast} placeholder="DLT template ID (optional)" />
          <label><input type="checkbox" checked={smsUnicode} onChange={(e) => setSmsUnicode(e.target.checked)} disabled={!canBroadcast} /> Unicode SMS</label>
        </div>
        <button style={sx.btn} disabled={!canBroadcast || busy === "broadcast"} onClick={sendBroadcast}>{busy === "broadcast" ? "Sending..." : "Send Broadcast"}</button>
        {sendResult ? <pre style={sx.pre}>{JSON.stringify(sendResult, null, 2)}</pre> : null}
      </section>

      <section style={sx.card}>
        <h3>Templates from Authkey</h3>
        <div style={sx.templates}>
          {templates.length ? templates.map((t, i) => (
            <button key={`${t.wid || "t"}_${i}`} style={sx.tpl} onClick={() => setWaWid(String(t.wid || ""))}>
              <strong>{t.wid || "-"}</strong>
              <span>{t.name || "Unnamed template"}</span>
            </button>
          )) : <span>No templates loaded.</span>}
        </div>
      </section>
    </div>
  );
}

const sx = {
  page: { padding: 20, display: "grid", gap: 14, fontFamily: "Inter, system-ui, sans-serif" },
  center: { minHeight: "70vh", display: "grid", placeItems: "center", fontFamily: "Inter, system-ui, sans-serif" },
  card: { border: "1px solid #e2e8f0", borderRadius: 12, padding: 14, background: "#fff" },
  grid: { display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" },
  row: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 },
  input: { width: "100%", border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 12px", fontSize: 14 },
  textarea: { width: "100%", border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 12px", minHeight: 90, fontSize: 14 },
  btn: { border: "none", borderRadius: 8, padding: "10px 12px", fontWeight: 700, background: "#f20c53", color: "#fff", cursor: "pointer", marginTop: 8 },
  btnAlt: { border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 12px", fontWeight: 700, background: "#fff", color: "#0f172a", cursor: "pointer", marginTop: 8 },
  toggleBtn: { border: "none", borderRadius: 999, padding: "10px 16px", fontWeight: 800, cursor: "pointer", marginTop: 8 },
  toggleSafe: { background: "#dcfce7", color: "#166534" },
  toggleWarn: { background: "#fee2e2", color: "#b91c1c" },
  notice: { borderRadius: 8, padding: "10px 12px", fontWeight: 600 },
  ok: { background: "#ecfdf5", border: "1px solid #86efac", color: "#166534" },
  err: { background: "#fff1f2", border: "1px solid #fca5a5", color: "#b91c1c" },
  popupCard: { position: "fixed", right: 20, top: 20, zIndex: 9999, minWidth: 320, maxWidth: 420, borderRadius: 14, boxShadow: "0 20px 40px rgba(15,23,42,0.18)", display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", border: "1px solid transparent" },
  popupOk: { background: "#f0fdf4", borderColor: "#86efac", color: "#166534" },
  popupErr: { background: "#fff1f2", borderColor: "#fca5a5", color: "#b91c1c" },
  popupBody: { display: "grid", gap: 4, flex: 1 },
  popupText: { fontSize: 13, lineHeight: 1.45 },
  popupClose: { border: "none", background: "transparent", color: "inherit", cursor: "pointer", fontWeight: 800, fontSize: 16, lineHeight: 1 },
  meta: { color: "#475569", fontSize: 13, alignSelf: "center" },
  statusBlock: { display: "grid", gap: 8, alignContent: "start" },
  statusPill: { display: "inline-flex", width: "fit-content", borderRadius: 999, padding: "8px 12px", fontSize: 13, fontWeight: 800 },
  statusOn: { background: "#dcfce7", color: "#166534" },
  statusOff: { background: "#fee2e2", color: "#b91c1c" },
  pre: { marginTop: 8, background: "#0f172a", color: "#e2e8f0", borderRadius: 8, padding: 10, overflow: "auto" },
  templates: { display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" },
  tpl: { textAlign: "left", border: "1px solid #cbd5e1", borderRadius: 8, background: "#f8fafc", padding: 10, display: "grid", gap: 4, cursor: "pointer" },
};
